import { IonButton, IonIcon } from "@ionic/react";
import { forwardRef } from "react";
import { type VariantProps } from "cva";
import { usePreferredHaptics } from "@/lib/hooks/usePreferredHaptics";
import { arrowRightIcon } from "@/lib/icons/@heroicons/react/16/solid";
import { cva, cx } from "@/lib/style/cva.config";

const buttonVariants = cva({
	base: "",
	variants: {
		variant: {
			primary: "ion-bg-orange-500 ion-text-white",
			secondary:
				"ion-bg-brown-100 ion-text-brown-700 ion-bg-h-brown-100 ion-opacity-bg-h-80 ion-bg-a-brown-100 ion-opacity-bg-a-70",
			header:
				"ion-bg-brown-300 ion-text-brown-600 ion-bg-h-brown-300 ion-opacity-bg-h-90 ion-bg-a-brown-300 ion-opacity-bg-a-80",
			link: "ion-text-brown-700 hover:underline",
			text: "text-brown-600 !ion-pl-0 flex items-center ion-bg-transparent part-native:bg-transparent",
			clear: "",
		},

		size: {
			default: "!ion-px-7 !ion-py-3.5 !ion-rounded-2xl part-native:font-bold part-native:text-lg",
			small:
				"!ion-py-2.5 !ion-rounded-lg part-native:min-h-10 part-native:text-md part-native:font-semibold",
			text: "!ion-px-0 !ion-py-0 part-native:text-sm font-semibold w-fit text-[#847369]",
			icon: "part-native:size-12 !ion-rounded-full !ion-p-1",
			header:
				"part-icon:m-0 part-native:size-10 part-native:min-h-0 !ion-rounded-full !ion-p-1 part-native:text-xs part-native:min-h-0",
		},
	},
	defaultVariants: {
		variant: "primary",
		size: "default",
	},
});
type ButtonIcon = "arrow-right";

export type ButtonProps = Omit<React.ComponentProps<typeof IonButton>, "size"> &
	VariantProps<typeof buttonVariants> & {
		icon?: ButtonIcon;
		customIcon?: string;
	};

export const Button = forwardRef<HTMLIonButtonElement, ButtonProps>(function Button(
	{ className, variant, size, icon, customIcon, fill, ...props },
	ref,
) {
	const PreferredHaptics = usePreferredHaptics();

	return (
		<IonButton
			ref={ref}
			{...props}
			fill={
				fill ??
				(variant === "secondary" || variant === "link" || variant === "text" || variant === "clear"
					? "clear"
					: "solid")
			}
			data-touch-target
			className={buttonVariants({
				size,
				variant,
				className: cx("m-0 cursor-pointer", className),
			})}
			onClick={(event) => {
				PreferredHaptics.impact();
				props.onClick?.(event);
			}}
		>
			{props.children}
			{customIcon && <IonIcon slot="end" name={customIcon} />}
			{icon === "arrow-right" && (
				<IonIcon
					slot="end"
					className={cx("ml-2 size-5 opacity-60", variant === "text" && "size-4")}
					icon={arrowRightIcon}
				/>
			)}
		</IonButton>
	);
});
